import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import i18n from 'i18next';

import { actionCloseShiftHandover } from 'lib/actions';
import { getShiftHandoverOpen } from 'lib/selectors/getShiftHandoverOpen';
import { SHIFT_HANDOVER_HOST } from 'lib/appConfig';
import { getClockOutOperator } from 'lib/selectors/getClockOutOperator';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';

const EmbeddedShiftHandover = ({ id, operator, onClose }) => {
  const [path, setPath] = useState('/handover');

  useEmbeddedEffect(id, 'shift-handover:close', () => {
    onClose();
  });

  useEmbeddedEffect(id, 'set-path', (data) => {
    if (data?.path) {
      setPath(data.path);
    }
  });

  useEmbeddedEffect(id, 'shift-handover:handle-submit', () => {
    onClose();
  });

  const context = {
    operatorId: operator?.id, // Internal operator ID
    operatorErpId: operator?.erpId, // ERP ID (personId from labor tickets)
    operatorName: operator?.name,
  };

  return (
    <MMEmbeddableZoneWithLoader
      id={id}
      src={`${SHIFT_HANDOVER_HOST}`}
      path={path}
      context={context}
      allow="microphone"
      params={{
        language: i18n.language,
      }}
    />
  );
};

export const ShiftHandoverWrapper = () => {
  const id = 'shift-handover';
  const dispatch = useDispatch();
  const shiftHandoverOpen = useSelector(getShiftHandoverOpen);
  const clockOutOperator = useSelector(getClockOutOperator);
  const latestOperator = useSelector(getLatestOperator);

  // clockOutOperator is the erpId
  const operatorErpId = clockOutOperator || latestOperator?.erpId;

  // Build operator object with internal id, erpId and name for the embed context.
  const operator = {
    id: latestOperator?.id, // Internal operator ID
    erpId: operatorErpId, // ERP ID (personId)
    name: latestOperator?.name || operatorErpId, // Fallback to erpId if name not available
  };

  // Handover is opened only after the operator has already been clocked out (see
  // ClockOutModal single-ticket path and ClockOutEmbedWrapper 'clocked-out'
  // handler), so this screen is presentational -- every way it ends just closes
  // the drawer. No timesheet mutation here.
  const handleClose = () => {
    dispatch(actionCloseShiftHandover());
  };

  return (
    <MMDrawer
      isOpen={shiftHandoverOpen}
      onClose={handleClose}
      title="Shift Handover"
    >
      <EmbeddedShiftHandover
        id={id}
        operator={operator}
        onClose={handleClose}
      />
    </MMDrawer>
  );
};
